import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, CheckCircle, DownloadSimple, DeviceMobile, Spinner, Wallet, Warning, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError } from "../lib/api";
import { preflightMobileCapture, uploadOmrBatch } from "../lib/omrUpload";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

const MOBILE_PREFLIGHT_LONGEST_SIDE = 1280;

function loadSelectedImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Nao foi possivel preparar a foto para validacao"));
    };
    image.src = url;
  });
}

async function createMobilePreflightPreview(file) {
  const image = await loadSelectedImage(file);
  const sourceWidth = Number(image.naturalWidth || image.width || 0);
  const sourceHeight = Number(image.naturalHeight || image.height || 0);
  if (!sourceWidth || !sourceHeight) throw new Error("A foto nao tem dimensoes validas");
  const scale = Math.min(1, MOBILE_PREFLIGHT_LONGEST_SIDE / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Nao foi possivel preparar a validacao da foto");
  context.drawImage(image, 0, 0, width, height);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.8));
  if (!blob) throw new Error("Nao foi possivel criar a pre-visualizacao da foto");
  return {
    file: new File([blob], "preflight-cartao.jpg", { type: "image/jpeg" }),
    sourceWidth,
    sourceHeight,
  };
}

// These are the same six mobile fiducials printed on the official card and
// validated by backend/omr/processor.py.  Keeping the guide tied to the real
// page geometry is much more useful than a generic document rectangle.
const MOBILE_CARD_TARGETS = [
  { x: 0.25, y: 0.0226 }, { x: 0.75, y: 0.0226 },
  { x: 0.028, y: 0.5 }, { x: 0.972, y: 0.5 },
  { x: 0.25, y: 0.9774 }, { x: 0.75, y: 0.9774 },
];
const CAMERA_FRAME = { x: 0.09, y: 0.065, width: 0.82, height: 0.87 };
const CAMERA_SAMPLE = { width: 480, height: 640 };
const emptyCameraGuide = {
  found: 0,
  expected: MOBILE_CARD_TARGETS.length,
  status: "preparing",
  brightness: 0,
  contrast: 0,
  markers: MOBILE_CARD_TARGETS.map(() => false),
};

const clamp = (value, minimum, maximum) => Math.max(minimum, Math.min(maximum, value));

function drawVideoCover(context, video, width, height) {
  const sourceWidth = Number(video.videoWidth || 0);
  const sourceHeight = Number(video.videoHeight || 0);
  if (!sourceWidth || !sourceHeight) return false;
  const scale = Math.max(width / sourceWidth, height / sourceHeight);
  const drawWidth = sourceWidth * scale;
  const drawHeight = sourceHeight * scale;
  context.drawImage(video, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
  return true;
}

function evaluateLiveCardGuide(video, canvas) {
  const context = canvas?.getContext?.("2d", { willReadFrequently: true });
  if (!context || !video?.videoWidth || !video?.videoHeight) return emptyCameraGuide;
  const { width, height } = CAMERA_SAMPLE;
  if (canvas.width !== width) canvas.width = width;
  if (canvas.height !== height) canvas.height = height;
  context.clearRect(0, 0, width, height);
  if (!drawVideoCover(context, video, width, height)) return emptyCameraGuide;

  let image;
  try {
    image = context.getImageData(0, 0, width, height).data;
  } catch (_) {
    return emptyCameraGuide;
  }
  const luminance = (x, y) => {
    const px = clamp(Math.round(x), 0, width - 1);
    const py = clamp(Math.round(y), 0, height - 1);
    const offset = (py * width + px) * 4;
    return (image[offset] * 0.2126) + (image[offset + 1] * 0.7152) + (image[offset + 2] * 0.0722);
  };

  let sampled = 0;
  let total = 0;
  let squaredTotal = 0;
  for (let y = 8; y < height; y += 16) {
    for (let x = 8; x < width; x += 16) {
      const value = luminance(x, y);
      sampled += 1;
      total += value;
      squaredTotal += value * value;
    }
  }
  const brightness = sampled ? total / sampled : 0;
  const contrast = sampled ? Math.sqrt(Math.max(0, (squaredTotal / sampled) - (brightness * brightness))) : 0;
  const sheet = {
    x: width * CAMERA_FRAME.x,
    y: height * CAMERA_FRAME.y,
    width: width * CAMERA_FRAME.width,
    height: height * CAMERA_FRAME.height,
  };
  const radius = Math.max(8, Math.round(sheet.width * 0.033));
  const innerRadius = Math.max(3, Math.round(radius * 0.42));
  const markers = MOBILE_CARD_TARGETS.map((target) => {
    const centerX = sheet.x + (sheet.width * target.x);
    const centerY = sheet.y + (sheet.height * target.y);
    let ringPixels = 0;
    let ringDark = 0;
    let centerPixels = 0;
    let centerWhite = 0;
    for (let y = Math.floor(centerY - radius); y <= Math.ceil(centerY + radius); y += 1) {
      for (let x = Math.floor(centerX - radius); x <= Math.ceil(centerX + radius); x += 1) {
        const distance = Math.hypot(x - centerX, y - centerY);
        if (distance > radius) continue;
        const value = luminance(x, y);
        if (distance <= innerRadius) {
          centerPixels += 1;
          if (value > 165) centerWhite += 1;
        } else {
          ringPixels += 1;
          if (value < 135) ringDark += 1;
        }
      }
    }
    const darkRatio = ringPixels ? ringDark / ringPixels : 0;
    const whiteRatio = centerPixels ? centerWhite / centerPixels : 0;
    return darkRatio >= 0.19 && whiteRatio >= 0.38;
  });
  const found = markers.filter(Boolean).length;
  const tooDark = brightness < 58 || contrast < 18;
  const glare = brightness > 242;
  const status = tooDark || glare ? "light" : found >= 5 ? "good" : found >= 4 ? "warning" : "poor";
  return {
    found,
    expected: MOBILE_CARD_TARGETS.length,
    status,
    brightness: Math.round(brightness),
    contrast: Math.round(contrast),
    markers,
  };
}

function cameraGuideCopy(guide) {
  if (guide.status === "preparing") return "Preparando a câmera…";
  if (guide.status === "light") return guide.brightness > 242 ? "Reduza o reflexo e incline levemente o celular." : "Aumente a luz e mantenha a folha sem sombra.";
  if (guide.found < 4) return "Aproxime a folha e alinhe os seis círculos impressos aos seis alvos.";
  if (guide.found < 5) return "Quase pronto. Ajuste a inclinação até encontrar mais uma baliza.";
  return "Cartão bem posicionado. Pode fotografar.";
}

export default function MobileCapturePage() {
  const inputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const preflightRequestRef = useRef(0);
  const previewFrameRef = useRef(null);
  const videoRef = useRef(null);
  const guideCanvasRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const guideTimerRef = useRef(null);
  const cameraActiveRef = useRef(false);
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [examId, setExamId] = useState("");
  const [classId, setClassId] = useState("auto");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState("idle");
  const [result, setResult] = useState(null);
  const [credits, setCredits] = useState(null);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [preflight, setPreflight] = useState({ status: "idle" });
  const [manualCalibration, setManualCalibration] = useState(false);
  const [manualCorners, setManualCorners] = useState([
    { x: 0.05, y: 0.05 }, { x: 0.95, y: 0.05 },
    { x: 0.95, y: 0.95 }, { x: 0.05, y: 0.95 },
  ]);
  const [manualCornerTouches, setManualCornerTouches] = useState([]);
  const [draggingCorner, setDraggingCorner] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraGuide, setCameraGuide] = useState(emptyCameraGuide);

  useEffect(() => {
    Promise.all([api.get("/exams"), api.get("/classes")])
      .then(([examResponse, classResponse]) => {
        const writable = (examResponse.data || []).filter((item) => item.can_write !== false);
        setExams(writable);
        setClasses(classResponse.data || []);
        if (writable.length) setExamId(writable[0].id);
      })
      .catch((error) => toast.error(fmtError(error)));
    const handleInstall = (event) => { event.preventDefault(); setInstallPrompt(event); };
    window.addEventListener("beforeinstallprompt", handleInstall);
    return () => window.removeEventListener("beforeinstallprompt", handleInstall);
  }, []);

  useEffect(() => {
    if (!examId) return;
    api.get(`/credits/me?exam_id=${encodeURIComponent(examId)}`)
      .then((response) => setCredits(response.data))
      .catch(() => setCredits(null));
    setClassId("auto");
  }, [examId]);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  useEffect(() => () => {
    cameraActiveRef.current = false;
    if (guideTimerRef.current) window.clearTimeout(guideTimerRef.current);
    cameraStreamRef.current?.getTracks?.().forEach((track) => track.stop());
  }, []);

  const exam = exams.find((item) => item.id === examId);
  const classOptions = useMemo(() => {
    const ids = new Set(exam?.class_ids || (exam?.class_id ? [exam.class_id] : []));
    return ids.size ? classes.filter((item) => ids.has(item.id)) : classes;
  }, [exam, classes]);

  const runPreflight = async (selected, corners = null) => {
    if (!selected) return;
    const requestId = preflightRequestRef.current + 1;
    preflightRequestRef.current = requestId;
    setPreflight({ status: "checking", summary: "Validando a foto antes da correcao..." });
    try {
      const prepared = await createMobilePreflightPreview(selected);
      const response = await preflightMobileCapture(prepared.file, {
        examId,
        sourceWidth: prepared.sourceWidth,
        sourceHeight: prepared.sourceHeight,
        manualCorners: corners,
      });
      if (requestId === preflightRequestRef.current) setPreflight(response.data || { status: "unavailable" });
    } catch (error) {
      if (requestId === preflightRequestRef.current) {
        setPreflight({
          status: "unavailable",
          can_process: true,
          summary: "Nao foi possivel validar esta foto agora. Voce pode corrigir mesmo assim.",
          warnings: [fmtError(error)],
          blockers: [],
        });
      }
    }
  };

  const chooseFile = async (selected) => {
    if (!selected) return;
    if (preview) URL.revokeObjectURL(preview);
    setFile(selected);
    setPreview(URL.createObjectURL(selected));
    setResult(null);
    setPhase("ready");
    setProgress(0);
    setManualCalibration(false);
    setManualCornerTouches([]);
    setDraggingCorner(null);
    const initialCorners = [
      { x: 0.05, y: 0.05 }, { x: 0.95, y: 0.05 },
      { x: 0.95, y: 0.95 }, { x: 0.05, y: 0.95 },
    ];
    setManualCorners(initialCorners);
    await runPreflight(selected);
  };

  const updateManualCorner = (index, event) => {
    const frame = previewFrameRef.current;
    if (index === null || !frame) return;
    const rect = frame.getBoundingClientRect();
    const x = Math.max(0.005, Math.min(0.995, (event.clientX - rect.left) / rect.width));
    const y = Math.max(0.005, Math.min(0.995, (event.clientY - rect.top) / rect.height));
    setManualCorners((current) => current.map((point, pointIndex) => (
      pointIndex === index ? { x, y } : point
    )));
  };

  const finishManualCorner = async (event) => {
    if (draggingCorner === null) return;
    const frame = previewFrameRef.current;
    if (!frame || !file) return;
    const rect = frame.getBoundingClientRect();
    const x = Math.max(0.005, Math.min(0.995, (event.clientX - rect.left) / rect.width));
    const y = Math.max(0.005, Math.min(0.995, (event.clientY - rect.top) / rect.height));
    const next = manualCorners.map((point, index) => (
      index === draggingCorner ? { x, y } : point
    ));
    setManualCorners(next);
    setManualCornerTouches((current) => current.includes(draggingCorner) ? current : [...current, draggingCorner]);
    setDraggingCorner(null);
    await runPreflight(file, next);
  };

  const stopGuidedCamera = () => {
    cameraActiveRef.current = false;
    if (guideTimerRef.current) window.clearTimeout(guideTimerRef.current);
    guideTimerRef.current = null;
    const stream = cameraStreamRef.current;
    cameraStreamRef.current = null;
    stream?.getTracks?.().forEach((track) => track.stop());
    if (videoRef.current) {
      videoRef.current.pause?.();
      videoRef.current.srcObject = null;
    }
    setCameraOpen(false);
    setCameraGuide(emptyCameraGuide);
  };

  const refreshCameraGuide = () => {
    if (!cameraActiveRef.current) return;
    const guide = evaluateLiveCardGuide(videoRef.current, guideCanvasRef.current);
    setCameraGuide(guide);
    guideTimerRef.current = window.setTimeout(refreshCameraGuide, 180);
  };

  const openGuidedCamera = async () => {
    if (!examId || busy) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Este navegador não permite câmera ao vivo. Use a opção de enviar uma foto.");
      inputRef.current?.click();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 2560 },
          aspectRatio: { ideal: 0.75 },
        },
      });
      cameraStreamRef.current = stream;
      cameraActiveRef.current = true;
      setCameraGuide(emptyCameraGuide);
      setCameraOpen(true);
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      const video = videoRef.current;
      if (!video) throw new Error("Não foi possível iniciar a visualização da câmera");
      video.srcObject = stream;
      await video.play();
      refreshCameraGuide();
    } catch (error) {
      cameraActiveRef.current = false;
      cameraStreamRef.current?.getTracks?.().forEach((track) => track.stop());
      cameraStreamRef.current = null;
      setCameraOpen(false);
      if (error?.name === "NotAllowedError") toast.error("Permita o uso da câmera para fazer a captura guiada.");
      else toast.error("Não foi possível abrir a câmera. Você ainda pode enviar uma foto já tirada.");
    }
  };

  const captureGuidedPhoto = () => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video?.videoHeight) {
      toast.error("A câmera ainda está preparando a imagem.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) {
      toast.error("Não foi possível capturar a foto.");
      return;
    }
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error("Não foi possível criar a foto do cartão.");
        return;
      }
      const captured = new File([blob], `cartao_guiado_${Date.now()}.jpg`, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });
      stopGuidedCamera();
      chooseFile(captured);
    }, "image/jpeg", 0.93);
  };

  const clearCapture = () => {
    stopGuidedCamera();
    preflightRequestRef.current += 1;
    if (preview) URL.revokeObjectURL(preview);
    setPreview("");
    setFile(null);
    setResult(null);
    setPhase("idle");
    setProgress(0);
    setPreflight({ status: "idle" });
    setManualCalibration(false);
    setManualCornerTouches([]);
    setDraggingCorner(null);
    if (inputRef.current) inputRef.current.value = "";
    if (galleryInputRef.current) galleryInputRef.current.value = "";
  };

  const refreshCredits = () => {
    if (!examId) return;
    api.get(`/credits/me?exam_id=${encodeURIComponent(examId)}`)
      .then((response) => setCredits(response.data))
      .catch(() => {});
  };

  const processCapture = async (force = false) => {
    if (!file || !examId || ["uploading", "processing"].includes(phase)) return;
    if (preflight.status === "checking") {
      toast.message("Aguarde a validacao da foto terminar.");
      return;
    }
    if (preflight.status === "retake" && !force) {
      toast.error("Tire outra foto: esta imagem nao passou na verificacao inicial.");
      return;
    }
    if (manualCalibration && manualCornerTouches.length !== 4) {
      toast.error("Posicione as quatro marcas pretas antes de usar a calibração manual.");
      return;
    }
    setPhase("uploading");
    setResult(null);
    try {
      const response = await uploadOmrBatch([file], {
        examId,
        classId: classId !== "auto" ? classId : null,
      scannerId: "mobile-camera",
      manualCorners: manualCalibration ? manualCorners : null,
        onProgress: setProgress,
      });
      const jobId = response.data.job_id;
      if (!jobId) throw new Error("O servidor nao retornou o processamento");
      setPhase("processing");
      setProgress(0);
      while (true) {
        await wait(1200);
        const jobResponse = await api.get(`/omr/jobs/${jobId}`);
        const job = jobResponse.data;
        const total = Math.max(Number(job.total_pages || job.total_files || 1), 1);
        setProgress(Math.min(100, Math.round((Number(job.processed_pages || 0) / total) * 100)));
        if (job.status === "failed") throw new Error(job.error || "Falha no processamento");
        if (job.status === "completed") {
          const first = (job.results || [])[0];
          if (!first?.id) throw new Error((job.errors || [])[0]?.error || "Nenhum cartao foi reconhecido");
          setResult({ ...first, correction_id: first.id, billing: job.billing });
          setPhase("completed");
          setProgress(100);
          toast.success("Cartao corrigido");
          refreshCredits();
          break;
        }
      }
    } catch (error) {
      setPhase("ready");
      toast.error(fmtError(error));
    }
  };

  const downloadCard = async () => {
    if (!examId) return;
    try {
      const response = await api.get(`/exams/${examId}/form.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `cartao_${exam?.name || examId}.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) { toast.error(fmtError(error)); }
  };

  const install = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    setInstallPrompt(null);
  };

  const busy = phase === "uploading" || phase === "processing";
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-7 sm:py-10" data-testid="mobile-capture-page">
      <div className="border-b border-border pb-6 mb-6">
        <div className="label-eyebrow mb-2">pwa · correcao individual</div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Câmera SAKI/Xm.</h1>
            <p className="text-sm text-muted-foreground mt-3">Fotografe um cartao por vez. O arquivo e enviado por HTTPS e processado pelo mesmo OMR das importacoes.</p>
          </div>
          <DeviceMobile size={42} weight="duotone" className="text-primary shrink-0" />
        </div>
        {installPrompt && <Button variant="outline" onClick={install} className="rounded-none mt-4 w-full sm:w-auto">Instalar aplicativo</Button>}
      </div>

      <div className="border border-border bg-card p-4 sm:p-6 space-y-4">
        <div>
          <div className="label-eyebrow mb-1">prova</div>
          <Select value={examId} onValueChange={setExamId} disabled={busy}>
            <SelectTrigger className="rounded-none h-12"><SelectValue placeholder="Escolha a prova" /></SelectTrigger>
            <SelectContent>{exams.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {classOptions.length > 1 && <div>
          <div className="label-eyebrow mb-1">turma</div>
          <Select value={classId} onValueChange={setClassId} disabled={busy}>
            <SelectTrigger className="rounded-none h-12"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Identificar automaticamente</SelectItem>
              {classOptions.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>}

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="border border-border p-3"><Wallet size={18} className="text-primary mb-2" /><b>{credits?.unlimited ? "Ilimitado" : credits?.balance ?? "--"}</b><span className="block text-xs text-muted-foreground">creditos disponiveis</span></div>
          <button type="button" onClick={downloadCard} disabled={!examId} className="border border-border p-3 text-left hover:border-primary disabled:opacity-50"><DownloadSimple size={18} className="text-primary mb-2" /><b>Baixar cartao</b><span className="block text-xs text-muted-foreground">modelo desta prova</span></button>
        </div>

        {cameraOpen ? <div className="fixed inset-0 z-50 bg-black text-white flex flex-col" data-testid="mobile-live-card-guide">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/20 bg-black/90">
            <div>
              <div className="font-display text-lg font-black">Alinhar cartão</div>
              <div className="text-xs text-white/70">Use os seis círculos impressos como pontos de fixação.</div>
            </div>
            <button type="button" onClick={stopGuidedCamera} className="border border-white/40 px-3 py-2 text-xs font-mono-data uppercase tracking-wider">Cancelar</button>
          </div>
          <div className="flex-1 min-h-0 flex items-center justify-center p-3 sm:p-5">
            <div className="relative w-full max-w-[560px] h-full max-h-[calc(100vh-196px)] aspect-[3/4] bg-black overflow-hidden shadow-2xl">
              <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" aria-label="Visualização ao vivo da câmera" />
              <div className={`absolute border-2 ${cameraGuide.status === "good" ? "border-emerald-400" : cameraGuide.status === "poor" || cameraGuide.status === "light" ? "border-amber-300" : "border-sky-300"}`} style={{ left: "9%", top: "6.5%", width: "82%", height: "87%" }} data-testid="mobile-card-fiducial-frame">
                {MOBILE_CARD_TARGETS.map((target, index) => {
                  const detected = cameraGuide.markers[index];
                  const markerClass = detected ? "border-emerald-300 bg-emerald-400/20" : "border-sky-200 bg-black/30";
                  return <span key={`${target.x}-${target.y}`} className={`absolute w-7 h-7 sm:w-8 sm:h-8 rounded-full border-[3px] ${markerClass} shadow-[0_0_0_2px_rgba(0,0,0,.42)]`} style={{ left: `${target.x * 100}%`, top: `${target.y * 100}%`, transform: "translate(-50%, -50%)" }} aria-label={`Baliza ${index + 1} ${detected ? "alinhada" : "a alinhar"}`} />;
                })}
                <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-12 h-12 border border-white/40 rounded-full opacity-60" aria-hidden="true" />
              </div>
              <canvas ref={guideCanvasRef} className="hidden" aria-hidden="true" />
              <div className="absolute left-3 right-3 bottom-3 bg-black/75 border border-white/20 p-3 backdrop-blur-sm">
                <div className="flex items-center justify-between gap-3 text-xs font-mono-data uppercase tracking-wider">
                  <span>Balizas {cameraGuide.found}/{cameraGuide.expected}</span>
                  <span className={cameraGuide.status === "good" ? "text-emerald-300" : cameraGuide.status === "poor" || cameraGuide.status === "light" ? "text-amber-200" : "text-sky-200"}>{cameraGuide.status === "good" ? "Pronto" : "Ajustando"}</span>
                </div>
                <p className="mt-2 text-sm leading-snug">{cameraGuideCopy(cameraGuide)}</p>
              </div>
            </div>
          </div>
          <div className="px-4 pb-5 pt-2 grid grid-cols-2 gap-3 bg-black/95">
            <button type="button" onClick={stopGuidedCamera} className="h-12 border border-white/40 text-xs font-mono-data uppercase tracking-wider">Cancelar</button>
            <button type="button" onClick={captureGuidedPhoto} className={`h-12 text-xs font-mono-data uppercase tracking-wider font-bold ${cameraGuide.status === "good" ? "bg-emerald-400 text-black" : "bg-primary text-primary-foreground"}`} data-testid="mobile-capture-guided-photo">
              Fotografar cartão
            </button>
          </div>
        </div> : !preview ? <div className="border-2 border-dashed border-primary bg-primary/5 min-h-[260px] flex flex-col items-center justify-center gap-4 p-6" data-testid="mobile-capture-start">
          <Camera size={52} weight="duotone" className="text-primary" />
          <div className="text-center">
            <div className="font-display text-2xl font-bold">Fotografar cartão</div>
            <div className="text-xs text-muted-foreground mt-2 max-w-md">Use a câmera nativa do celular para preservar a resolução original. Depois da foto, conferimos foco, luz, resolução e as marcas reais do cartão antes de corrigir.</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-md">
            <Button type="button" onClick={() => inputRef.current?.click()} disabled={!examId || busy} className="rounded-none h-12" data-testid="open-mobile-native-camera"><Camera size={18} className="mr-2" />Abrir câmera do celular</Button>
            <Button type="button" variant="outline" onClick={() => galleryInputRef.current?.click()} disabled={!examId || busy} className="rounded-none h-12">Escolher foto existente</Button>
          </div>
          <span className="text-xs text-muted-foreground text-center">Enquadre a folha inteira, deixe uma pequena margem nos quatro cantos e evite reflexos, sombra e dedos.</span>
        </div> : <div
          ref={previewFrameRef}
          className="relative border border-border bg-black touch-none select-none"
          onPointerMove={(event) => updateManualCorner(draggingCorner, event)}
          onPointerUp={finishManualCorner}
          onPointerCancel={() => setDraggingCorner(null)}
        >
          <img src={preview} alt="Cartao fotografado" className="block w-full h-auto" draggable="false" />
          {manualCalibration && manualCorners.map((point, index) => <button
            key={`manual-marker-${index}`}
            type="button"
            aria-label={`Mover marca preta ${index + 1}`}
            className="absolute -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full border-4 border-amber-300 bg-black/55 text-xs font-bold text-white shadow-[0_0_0_2px_rgba(0,0,0,.55)] touch-none"
            style={{ left: `${point.x * 100}%`, top: `${point.y * 100}%` }}
            onPointerDown={(event) => {
              event.preventDefault();
              event.currentTarget.setPointerCapture?.(event.pointerId);
              setDraggingCorner(index);
            }}
          >{index + 1}</button>)}
          {manualCalibration && <div className="absolute left-3 right-3 bottom-3 border border-amber-300/60 bg-black/80 p-3 text-xs text-white backdrop-blur-sm">
            Arraste cada número até o centro de uma marca preta impressa no cartão. A ordem é: superior esquerda, superior direita, inferior direita e inferior esquerda.
          </div>}
          {!busy && <button type="button" onClick={clearCapture} className="absolute top-3 right-3 bg-card border border-border p-2" aria-label="Descartar foto"><X size={18} /></button>}
        </div>}
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="hidden" onChange={(event) => chooseFile(event.target.files?.[0])} />
        <input ref={galleryInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(event) => chooseFile(event.target.files?.[0])} />

        {preview && !result && <div
          className={`border p-4 text-sm ${preflight.status === "ready" ? "border-[hsl(var(--success))] bg-[hsl(var(--success))]/5" : preflight.status === "retake" ? "border-destructive bg-destructive/5" : "border-[hsl(var(--warning))]/60 bg-[hsl(var(--warning))]/5"}`}
          data-testid="mobile-photo-preflight"
        >
          <div className="flex items-start gap-3">
            {preflight.status === "checking" ? <Spinner size={20} className="animate-spin shrink-0 text-primary" /> : preflight.status === "ready" ? <CheckCircle size={20} weight="fill" className="shrink-0 text-[hsl(var(--success))]" /> : <Warning size={20} weight="fill" className="shrink-0 text-[hsl(var(--warning))]" />}
            <div className="min-w-0">
              <b>{preflight.status === "checking" ? "Conferindo a foto..." : preflight.status === "ready" ? "Foto aprovada" : preflight.status === "retake" ? "Nova foto recomendada" : "Validação indisponível"}</b>
              <p className="mt-1 text-muted-foreground">{preflight.summary || "A validação vai analisar esta foto antes da correção."}</p>
              {[...(preflight.blockers || []), ...(preflight.warnings || [])].map((message) => <div key={message} className="mt-1 text-xs">• {message}</div>)}
              {preflight.checks?.corner_fiducials && <div className="mt-2 text-xs text-muted-foreground">Marcas de canto: {preflight.checks.corner_fiducials.found}/{preflight.checks.corner_fiducials.expected} · nitidez: {preflight.checks.sharpness?.laplacian_variance ?? "--"}</div>}
            </div>
          </div>
        </div>}

        {preview && !result && <div className="border border-border p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <b>Calibração manual pelas marcas pretas</b>
              <p className="text-xs text-muted-foreground mt-1">Use quando a leitura automática não encontrar corretamente os marcadores do cartão. {manualCalibration ? `${manualCornerTouches.length}/4 marcas posicionadas.` : ""}</p>
            </div>
            <Button
              type="button"
              variant={manualCalibration ? "default" : "outline"}
              className="rounded-none"
              disabled={busy}
              data-testid="mobile-manual-marker-calibration"
              onClick={() => {
                const next = !manualCalibration;
                setManualCalibration(next);
                if (next) window.setTimeout(() => previewFrameRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
                else runPreflight(file);
              }}
            >{manualCalibration ? "Remover ajuste manual" : "Ajustar marcas pretas"}</Button>
          </div>
        </div>}

        {preview && !result && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} className="rounded-none h-12">Tirar outra</Button>
          <Button onClick={() => processCapture(preflight.status === "retake")} disabled={busy || !examId || preflight.status === "checking"} className="rounded-none h-12">
            {busy ? <Spinner size={18} className="mr-2 animate-spin" /> : <Camera size={18} className="mr-2" />}
            {phase === "uploading" ? `Enviando ${progress}%` : phase === "processing" ? `Corrigindo ${progress}%` : preflight.status === "retake" ? "Corrigir mesmo assim" : "Corrigir agora"}
          </Button>
        </div>}
        {busy && <div className="h-2 bg-muted overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} /></div>}

        {result && <div className="border border-[hsl(var(--success))] bg-[hsl(var(--success))]/5 p-5">
          <div className="flex items-center gap-2 font-display text-2xl font-bold"><CheckCircle size={28} weight="fill" className="text-[hsl(var(--success))]" /> Correcao pronta</div>
          <div className="text-sm text-muted-foreground mt-2">CPF {result.cpf || "nao identificado"} · {result.marked_objective || 0} resposta(s) marcada(s)</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <Link to={`/correction/${result.correction_id}`} className="h-12 bg-primary text-primary-foreground flex items-center justify-center font-mono-data text-xs font-bold uppercase tracking-widest">Conferir resultado</Link>
            <Button variant="outline" onClick={clearCapture} className="rounded-none h-12">Proximo cartao</Button>
          </div>
        </div>}
      </div>

      <div className="mt-5 border border-[hsl(var(--warning))]/60 bg-[hsl(var(--warning))]/5 p-4 flex gap-3 text-sm">
        <Warning size={20} weight="fill" className="text-[hsl(var(--warning))] shrink-0" />
        <div><b>Foto original, verificação real.</b> A pré-validação usa apenas uma cópia temporária reduzida para conferir a imagem; a foto original só é enviada quando você confirma a correção.</div>
      </div>
    </div>
  );
}
